// Last edited: 2026-02-21 by Douglas — replaced inline notification logic with UnifiedNotificationBell

import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useParams, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import { useGamification } from '../contexts/GamificationContext';
import { GARAGES } from '../constants/garages';
import { getRouteInfo } from '../config/routes';
import { createScopedLogger } from '../utils/logger.js';
import { supabase } from '../lib/supabase';
import UnifiedNotificationBell from './notifications/UnifiedNotificationBell';
import CommsLivePanel, { useCommsUnhandledCount } from '../lots/comms/CommsLivePanel';
import SendFeedbackDialog from './SendFeedbackDialog';
import { GLASS_SURFACE } from './ui/glass';
import {
  IconLogout, IconUser, IconSettings, IconLogin, IconChevronRight, IconDeviceMobile,
  IconChartLine, IconTrophy, IconClipboard, IconCash, IconAward, IconStar, IconShirt, IconTool, IconRadio,
  IconMessage2
} from '@tabler/icons-react';


/**
 * Professional Dashboard Header
 * Black gradient with gold shimmer pulse, context-aware breadcrumbs
 */

// Gradient and pulse animations
const animations = `
  @keyframes gold-pulse {
    0% { opacity: 0.2; }
    50% { opacity: 0.5; }
    100% { opacity: 0.2; }
  }

  @keyframes shimmer-flow {
    0% { background-position: 0% 50%; }
    100% { background-position: 200% 50%; }
  }

  @keyframes metallic-shine {
    0% { background-position: -200% center; }
    100% { background-position: 200% center; }
  }

  @keyframes glow-pulse {
    0%, 100% {
      text-shadow: 0 0 10px rgba(174, 163, 109, 0.4),
                   0 0 20px rgba(174, 163, 109, 0.2),
                   0 0 30px rgba(174, 163, 109, 0.1);
    }
    50% {
      text-shadow: 0 0 20px rgba(174, 163, 109, 0.6),
                   0 0 30px rgba(174, 163, 109, 0.4),
                   0 0 40px rgba(174, 163, 109, 0.2),
                   0 0 50px rgba(174, 163, 109, 0.1);
    }
  }

  @keyframes subtle-float {
    0%, 100% { transform: translateY(0px); }
    50% { transform: translateY(-2px); }
  }
`;

if (typeof document !== 'undefined' && !document.getElementById('header-animations')) {
  const style = document.createElement('style');
  style.id = 'header-animations';
  style.innerHTML = animations + `
    .animate-gold-pulse {
      animation: gold-pulse 6s ease-in-out infinite;
    }
    .animate-shimmer-flow {
      animation: shimmer-flow 8s linear infinite;
    }
    .animate-metallic-shine {
      animation: metallic-shine 3s linear infinite;
    }
    .animate-glow-pulse {
      animation: glow-pulse 4s ease-in-out infinite;
    }
    .animate-subtle-float {
      animation: subtle-float 3s ease-in-out infinite;
    }
  `;
  document.head.appendChild(style);
}

const log = createScopedLogger('GarageHeader');

// Rank colors by badge type
const RANK_COLORS = {
  'rookie': 'text-gray-400',
  'parkologist': 'text-green-400',
  'pro': 'text-blue-400',
  'all-pro': 'text-purple-400',
  'legend': 'text-orange-400',
  'goat': 'text-vegas-gold',
  'immortal': 'text-vegas-gold animate-pulse'
};

// Icon map for valet nav
const VALET_NAV_ICONS = {
  IconChartLine,
  IconTrophy,
  IconClipboard,
  IconCash,
  IconAward,
  IconStar,
  IconShirt,
  IconTool
};

export default function GarageHeaderProfessional({ onShowAuth, onShowSettings }) {
  const { user, logout, valetProfile } = useAuth();
  const { totalXP, level, levelInfo } = useGamification();
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  // Explicit page feedback: opened only from the menu item below (Doug LOCK 2026-10-03).
  const [showFeedback, setShowFeedback] = useState(false);
  const [commsOpen, setCommsOpen] = useState(false);
  const { count: commsCount, hasCall: commsHasCall } = useCommsUnhandledCount();
  const [rankBadge, setRankBadge] = useState(null);

  // Fetch highest tier-progression badge (rank)
  useEffect(() => {
    if (!valetProfile?.id) {
      setRankBadge(null);
      return;
    }

    const fetchRank = async () => {
      try {
        const { supabase } = await import('../lib/supabase');
        const { data, error } = await supabase
          .from('badge_assignments')
          .select(`
            badge_type,
            badge_definitions!inner (
              name,
              icon,
              family,
              family_order
            )
          `)
          .eq('valet_id', valetProfile.id)
          .eq('is_active', true)
          .eq('badge_definitions.family', 'tier-progression')
          .order('badge_definitions(family_order)', { ascending: false })
          .limit(1)
          .maybeSingle();

        if (!error && data) {
          setRankBadge({
            type: data.badge_type,
            name: data.badge_definitions.name.replace(/^[^\s]+\s/, ''), // Remove emoji
            icon: data.badge_definitions.icon
          });
        }
      } catch (err) {
        console.error('[Header] Failed to fetch rank:', err);
      }
    };

    fetchRank();
  }, [valetProfile?.id]);


  const location = useLocation();
  const params = useParams();
  const navigate = useNavigate();
  const userRef = useRef(null);
  const dropdownRef = useRef(null);

  // Get current context from route config or URL params
  const routeInfo = getRouteInfo(location.pathname);

  // Use route config first, fallback to URL params
  let actualGarageId = routeInfo?.garageId || params.garageId;
  let actualLotId = routeInfo?.lotId || params.lotId;

  // If still no IDs, try parsing pathname manually
  if (!actualGarageId && location.pathname) {
    const pathParts = location.pathname.split('/').filter(p => p);
    if (pathParts.length >= 2) {
      actualGarageId = pathParts[0];
      actualLotId = pathParts[1];
    } else if (pathParts.length === 1) {
      actualGarageId = pathParts[0];
    }
  }

  const garage = GARAGES.find(g => g.id === actualGarageId);
  const lot = garage?.lots.find(l => l.id === actualLotId);

  const currentContext = {
    garage: garage?.name || 'ODV Platform',
    lot: lot?.name || null
  };

  // Check if we're in the valet garage
  const isValetGarage = actualGarageId === 'valet';
  const valetGarage = GARAGES.find(g => g.id === 'valet');
  const valetLots = valetGarage?.lots || [];

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!showUserDropdown) return;

    const handleClickOutside = (event) => {
      // Check if click is inside the profile button or the dropdown (which is in a portal)
      const isInsideButton = userRef.current?.contains(event.target);
      const isInsideDropdown = dropdownRef.current?.contains(event.target);

      if (!isInsideButton && !isInsideDropdown) {
        setShowUserDropdown(false);
      }
    };

    // Use mousedown to catch clicks before they propagate
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showUserDropdown]);


  return (
    <div className="flex flex-col">
      <header className="relative h-11 bg-gradient-to-r from-black via-gray-950 to-black border-b-2 border-vegas-gold/30 shadow-[0_2px_10px_rgba(174,163,109,0.3)] backdrop-blur-sm">
        {/* Pulsing gold shimmer overlay */}
        <div
          className="absolute inset-0 animate-gold-pulse pointer-events-none"
          style={{
            background: 'linear-gradient(90deg, transparent 0%, rgba(174,163,109,0.15) 25%, rgba(174,163,109,0.3) 50%, rgba(174,163,109,0.15) 75%, transparent 100%)',
            backgroundSize: '200% 100%',
          }}
        />

        {/* Animated shimmer effect */}
        <div
          className="absolute inset-0 animate-shimmer-flow pointer-events-none"
          style={{
            background: 'linear-gradient(90deg, transparent 0%, rgba(174,163,109,0.1) 40%, rgba(174,163,109,0.25) 50%, rgba(174,163,109,0.1) 60%, transparent 100%)',
            backgroundSize: '200% 100%',
          }}
        />

        <div className="relative flex items-center justify-between h-11 px-4">
        {/* Left side - Dynamic Context Breadcrumbs */}
        <div className="flex items-center space-x-2">
          {currentContext.lot ? (
            <>
              {/* Garage */}
              <span className="text-sm font-semibold uppercase tracking-wider text-vegas-gold">
                {currentContext.garage}
              </span>

              {/* Separator */}
              <IconChevronRight className="h-4 w-4 text-vegas-gold/50" />

              {/* Lot */}
              <span className="text-sm font-medium text-gray-400">
                {currentContext.lot}
              </span>
            </>
          ) : (
            <span className="text-sm font-semibold uppercase tracking-wider text-vegas-gold">
              {currentContext.garage}
            </span>
          )}
        </div>

        {/* Center - ODV Logo (Account for sidebar width - offset left to center on content area) */}
        <div className="absolute left-1/2 transform -translate-x-1/2 hidden md:block z-0 pointer-events-none" style={{marginLeft: '-112px'}}>
          <img
            src="/odv-logo-car.png"
            alt="Open Door Valet"
            className="h-9 object-contain drop-shadow-lg"
          />
        </div>

        {/* Right side - Settings & Auth */}
        <div className="relative z-10 flex items-center space-x-4">
          {/* Valet Mobile View Button - Show for all authenticated users */}
          {user && (
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                log.debug?.('Switching to valet mobile view');
                navigate('/valet');
              }}
              className="group relative p-1.5 text-vegas-gold hover:text-vegas-gold/80 hover:bg-vegas-gold/20 transition-all duration-200 rounded-lg"
              title="Switch to Valet Mobile View"
            >
              <IconDeviceMobile className="h-5 w-5" />
              {/* Tooltip */}
              <span className="absolute -bottom-8 right-0 px-2 py-1 bg-gray-900 text-xs text-white rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
                Valet View
              </span>
            </button>
          )}

          {/* Unified Notification Bell */}
          {user && <UnifiedNotificationBell />}

          {/* Live Comms Button */}
          {user && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                setCommsOpen(prev => !prev);
              }}
              className={`relative p-1.5 transition-all duration-200 rounded-lg ${
                commsHasCall
                  ? 'text-red-400 hover:text-red-300 hover:bg-red-400/20 animate-pulse'
                  : commsCount > 0
                    ? 'text-[#AEA36D] hover:text-[#AEA36D]/80 hover:bg-[#AEA36D]/20'
                    : 'text-gray-500 hover:text-gray-400 hover:bg-gray-700/50'
              }`}
              title={commsCount > 0 ? `${commsCount} unhandled comms` : 'Live comms feed'}
            >
              <IconRadio className="h-5 w-5" />
              {commsCount > 0 && (
                <span className={`absolute -top-1 -right-1 h-4 min-w-4 px-1 text-[10px] font-bold rounded-full flex items-center justify-center ${
                  commsHasCall ? 'bg-red-500 text-white' : 'bg-[#AEA36D] text-black'
                }`}>
                  {commsCount > 99 ? '99+' : commsCount}
                </span>
              )}
            </button>
          )}
          <CommsLivePanel open={commsOpen} onClose={() => setCommsOpen(false)} />

          {/* Settings Button */}
          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              log.debug?.('Settings button clicked');
              if (onShowSettings) {
                onShowSettings();
              } else {
                log.debug?.('Settings clicked - no handler provided');
                // Fallback: show a simple toast or navigate to settings
                toast('Settings functionality coming soon!');
              }
            }}
            className="p-1.5 text-vegas-gold hover:text-vegas-gold/80 hover:bg-vegas-gold/20 transition-all duration-200 rounded-lg"
            title="Settings"
          >
            <IconSettings className="h-5 w-5" />
          </button>

          {/* User Profile / Sign In */}
          {user ? (
            <div className="relative" ref={userRef}>
              {/* Profile Button - Click to toggle dropdown */}
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowUserDropdown(prev => !prev);
                }}
                className="flex items-center gap-2 px-2 py-1 rounded-xl cursor-pointer hover:bg-vegas-gold/20 transition-all"
              >
                {/* Avatar */}
                <div className="relative">
                  <img
                    src={user?.user_metadata?.avatar_url || ''}
                    alt=""
                    className="w-7 h-7 rounded-full object-cover border-2 border-vegas-gold/50"
                    referrerPolicy="no-referrer"
                    style={{ display: user?.user_metadata?.avatar_url ? 'block' : 'none' }}
                  />
                  {!user?.user_metadata?.avatar_url && (
                    <div className="w-7 h-7 bg-vegas-gold/20 rounded-full flex items-center justify-center">
                      <IconUser className="h-4 w-4 text-vegas-gold" />
                    </div>
                  )}
                </div>

                {/* Name & Rank */}
                <div className="hidden sm:block text-left">
                  <p className="text-white font-medium text-xs">
                    {(valetProfile?.first_name && valetProfile?.last_name)
                      ? `${valetProfile.first_name} ${valetProfile.last_name}`
                      : (user?.user_metadata?.full_name || user?.email?.split('@')[0] || 'User')
                    }
                  </p>
                  <p className="text-[10px]">
                    {rankBadge ? (
                      <>
                        <span className={RANK_COLORS[rankBadge.type] + ' font-semibold'}>
                          {rankBadge.icon} {rankBadge.name}
                        </span>
                        <span className="text-gray-500 mx-1">·</span>
                        <span className="text-vegas-gold">Lvl {level || valetProfile?.level || 1}</span>
                        <span className="text-gray-500 mx-1">·</span>
                        <span className="text-cyan-400">{(totalXP || 0).toLocaleString()} XP</span>
                      </>
                    ) : (
                      <>
                        <span className="text-vegas-gold">Lvl {level || valetProfile?.level || 1}</span>
                        <span className="text-gray-500 mx-1">·</span>
                        <span className="text-cyan-400">{(totalXP || 0).toLocaleString()} XP</span>
                      </>
                    )}
                  </p>
                </div>
              </button>

              {/* Dropdown Menu - rendered via Portal to escape overflow:hidden parents */}
              {showUserDropdown && createPortal(
                <div
                  ref={dropdownRef}
                  className={`w-64 overflow-hidden ${GLASS_SURFACE}`}
                  style={{
                    position: 'fixed',
                    top: '50px',
                    right: '20px',
                    zIndex: 99999
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  {/* GSEAT PRO Header */}
                  <div className="px-4 py-3 bg-white/5 border-b border-white/10">
                    <p className="text-vegas-gold font-bold text-sm tracking-wider">GSEAT PRO</p>
                    <p className="text-gray-400 text-xs mt-1">{user?.email}</p>
                  </div>

                  {/* Menu Items */}
                  <div className="p-2">
                    <button
                      onClick={() => {
                        setShowUserDropdown(false);
                        navigate('/profile');
                      }}
                      className="w-full flex items-center gap-3 px-3 py-2 text-sm text-white hover:bg-vegas-gold/20 hover:text-vegas-gold rounded-lg transition-colors"
                    >
                      <IconUser className="w-4 h-4" />
                      Profile
                    </button>
                    <button
                      onClick={() => {
                        setShowUserDropdown(false);
                        if (onShowSettings) onShowSettings();
                      }}
                      className="w-full flex items-center gap-3 px-3 py-2 text-sm text-white hover:bg-vegas-gold/20 hover:text-vegas-gold rounded-lg transition-colors"
                    >
                      <IconSettings className="w-4 h-4" />
                      Settings
                    </button>
                    <button
                      onClick={() => {
                        setShowUserDropdown(false);
                        setShowFeedback(true);
                      }}
                      className="w-full flex items-center gap-3 px-3 py-2 text-sm text-white hover:bg-vegas-gold/20 hover:text-vegas-gold rounded-lg transition-colors"
                      data-testid="menu-send-feedback"
                    >
                      <IconMessage2 className="w-4 h-4" />
                      Send feedback
                    </button>
                  </div>

                  {/* Sign Out */}
                  <div className="p-2 border-t border-white/10">
                    <button
                      onClick={async () => {
                        setShowUserDropdown(false);
                        sessionStorage.setItem('odv-stay-on-auth', 'true');
                        await logout();
                        navigate('/auth?stay=1');
                      }}
                      className="w-full flex items-center gap-3 px-3 py-2 text-sm text-vegas-gold hover:bg-vegas-gold/20 rounded-lg transition-colors font-medium"
                    >
                      <IconLogout className="w-4 h-4" />
                      Sign Out
                    </button>
                  </div>
                </div>,
                document.body
              )}
            </div>
          ) : (
            <button
              onClick={() => {
                if (onShowAuth) {
                  onShowAuth();
                }
              }}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-vegas-gold hover:bg-vegas-gold/80 text-black font-semibold rounded-lg transition-all duration-200"
            >
              <IconLogin className="w-4 h-4" />
              <span className="text-xs">Sign In</span>
            </button>
          )}
        </div>
        </div>
      </header>

      {/* Valet Navigation Bar - shows when in valet garage */}
      {isValetGarage && user && (
        <nav className="bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 border-b border-vegas-gold/20 px-3 py-1 overflow-x-auto">
          <div className="flex items-center gap-1 min-w-max">
            {valetLots.map((lotItem) => {
              const Icon = VALET_NAV_ICONS[lotItem.icon];
              const isActive = actualLotId === lotItem.id || (lotItem.path === '/valet' && !actualLotId);

              return (
                <button
                  key={lotItem.id}
                  onClick={() => navigate(lotItem.path)}
                  className={`
                    flex items-center gap-1.5 px-2 py-1 rounded-lg text-xs font-medium
                    transition-all duration-200 whitespace-nowrap
                    ${isActive
                      ? 'bg-vegas-gold/20 text-vegas-gold border border-vegas-gold/40 shadow-[0_0_10px_rgba(174,163,109,0.3)]'
                      : 'text-gray-400 hover:text-vegas-gold hover:bg-vegas-gold/10 border border-transparent'
                    }
                  `}
                >
                  {Icon && <Icon className="h-3.5 w-3.5" />}
                  <span className="hidden sm:inline">{lotItem.name}</span>
                </button>
              );
            })}
          </div>
        </nav>
      )}
      <SendFeedbackDialog open={showFeedback} onClose={() => setShowFeedback(false)} />
    </div>
  );
}